import { useEffect, useRef, useCallback } from 'react';
import { useSessionContext } from '../contexts/SessionContext';

/**
 * Hook that sends visibility reports to the server for player AI context.
 * Debounced at 2 seconds — collects visible entities from the current view
 * and sends a player:visibility-report WS message.
 */
const DEBOUNCE_MS = 2000;

export interface VisibilityReportData {
  visibleTokenIds: string[];
  visibleNoteIds: string[];
  visibleAnnotationIds: string[];
  currentPageId: string;
}

export function useVisibilityReport(
  getVisibleEntities: () => VisibilityReportData | null,
) {
  const { mp } = useSessionContext();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastReportRef = useRef<string>('');

  const sendReport = useCallback(() => {
    const data = getVisibleEntities();
    if (!data) return;

    // Dedup: skip if identical to last report
    const key = JSON.stringify(data);
    if (key === lastReportRef.current) return;
    lastReportRef.current = key;

    mp.send({
      type: 'player:visibility-report',
      visibleTokenIds: data.visibleTokenIds,
      visibleNoteIds: data.visibleNoteIds,
      visibleAnnotationIds: data.visibleAnnotationIds,
      currentPageId: data.currentPageId,
    });
  }, [mp, getVisibleEntities]);

  const scheduleReport = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(sendReport, DEBOUNCE_MS);
  }, [sendReport]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { scheduleReport };
}
